import { memo, useState, useEffect, useCallback, useMemo, useRef, type CSSProperties } from 'react'
import { ModalShell } from '../common/ModalShell.js'
import { playoutApi, type EndLivePlan, type GapFillOptions, type LiveFillEntry } from '../../lib/api.js'
import { useItems } from '../../store/index.js'
import { getServerNowMs } from '../../hooks/useServerClock.js'
import { adBlockTotalDurationSec } from '../../lib/adBlockTiming.js'
import type { ScheduleItem } from '../../types/index.js'
import { scheduleItemTitle } from '../../lib/utils.js'
import { Spinner } from '../common/Spinner.js'
import { formatAdSpotLabel } from '../../lib/adSpotLabel.js'
import { previewLiveHandover } from '../../lib/liveHandover.js'
// La boîte réutilise les classes .agenda-editor / .gap-fill de la grille : on
// importe la feuille explicitement pour ne pas dépendre du montage d'AgendaView.
import '../agenda/AgendaEditors.css'
import './LiveSkipDialog.css'

// ─── Types ────────────────────────────────────────────────────────────────────

interface LiveSkipDialogProps {
  channelId: string
  liveItem: ScheduleItem
  nextItem: ScheduleItem | null
  /**
   * Plan de coupure proposé par le serveur pour un direct « Fin manuelle ».
   * Quand il est fourni, la sélection démarre pré-remplie avec exactement ce que
   * « Fin du direct » poserait sans intervention : l'opérateur valide, retire ou
   * complète, mais il n'a jamais une boîte vide devant lui pendant que le direct
   * est encore à l'antenne.
   */
  initialPlan?: EndLivePlan | null
  onConfirm: (
    items: LiveFillEntry[],
    options: { compact: boolean; leadSec?: number; cutAt?: string; requireExactFill?: boolean },
  ) => void | Promise<void>
  onCancel: () => void
}

type FillKind = 'AD_BREAK' | 'INFOSERVICE' | 'JINGLE' | 'INTERLUDE'

/** Un élément posé dans le remplissage : ce qu'on affiche + ce qu'on envoie. */
interface FillItem {
  uid:         string
  label:       string
  sublabel:    string
  durationSec: number
  kind:        FillKind
  /** Élément élastique : sa durée est celle qui reste, calculée à l'insertion. */
  fillRest?:   boolean
  entry:       LiveFillEntry
}

type CatalogTab = 'ADS' | 'INFOSERVICE' | 'JINGLES' | 'COMING_NEXT'

const CATALOG_TABS: Array<{ id: CatalogTab; label: string }> = [
  { id: 'ADS',         label: 'Publicités' },
  { id: 'INFOSERVICE', label: 'Infoservice' },
  { id: 'JINGLES',     label: 'Éléments d’antenne' },
  { id: 'COMING_NEXT', label: 'Coming next' },
]

const KIND_BADGES: Record<FillKind, string> = {
  AD_BREAK:    'PUB',
  INFOSERVICE: 'INFO',
  JINGLE:      'JGL',
  INTERLUDE:   'INT',
}

/** Types de programmes d'habillage rangés dans l'onglet « Coming next ». */
const COMING_NEXT_TYPES = ['COMING_NEXT', 'BANDE_ANNONCE']

/**
 * Délai entre la confirmation et la coupure effective du direct. Le remplissage
 * ne commence pas maintenant mais après ce délai de préchargement, il faut donc
 * le déduire du trou annoncé à l'opérateur. Il est envoyé explicitement au
 * serveur (`leadSec`) : l'heure affichée et la coupure réelle ne peuvent plus
 * diverger si le défaut serveur (LIVE_SKIP_DEFAULT_LEAD_SEC) venait à changer.
 */
const LIVE_SKIP_LEAD_SEC = 3

/** Bornes serveur du préchargement (LIVE_SKIP_MIN/MAX_LEAD_SEC). */
const LIVE_SKIP_MIN_LEAD_SEC = 3
/**
 * Fenêtre dans laquelle la coupure armée est envoyée au serveur. L'heure exacte
 * reste `autoCutMs` : cette avance sert uniquement au préchargement.
 */
const AUTO_ARM_FIRE_WINDOW_SEC = 60

/** Types à heure fixe : ils bornent le trou et ne bougent jamais. */
const ANCHOR_TYPES = ['PROGRAM', 'LIVE']

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtTime(iso: string | number) {
  return new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
}

function fmtSec(sec: number) {
  const s = Math.max(0, Math.floor(sec))
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), ss = s % 60
  if (h > 0) return `${h}h${String(m).padStart(2, '0')}m${String(ss).padStart(2, '0')}s`
  return `${m}m${String(ss).padStart(2, '0')}s`
}

function itemTitle(item: ScheduleItem | null): string {
  if (!item) return 'Fin de grille'
  return scheduleItemTitle(item, 'Programme suivant') ?? 'Programme suivant'
}

let uidSeq = 0
const nextUid = () => `fill_${++uidSeq}`

/** Badge court d'un item de grille déjà posé (tampon). */
function bufferBadge(item: ScheduleItem): string {
  return KIND_BADGES[item.itemType as FillKind] ?? 'ITEM'
}

// ─── Tampons déjà programmés ──────────────────────────────────────────────────

/**
 * Les pubs, jingles et infoservice déjà posés entre le direct et le prochain
 * programme. Ils ne sont pas modifiables ici — seulement montrés à leur place
 * réelle, avant ou après le remplissage selon le compactage.
 */
const BufferRows = memo(function BufferRows({ items, moved = false }: { items: ScheduleItem[]; moved?: boolean }) {
  return (
    <div className="live-skip__buffers" data-moved={moved || undefined}>
      {items.map(item => (
        <div key={item.id} className="gap-fill__selected-row live-skip__buffer-row">
          <div className="gap-fill__selected-main">
            <span className={`gap-fill__kind is-${item.itemType.toLowerCase()}`}>{bufferBadge(item)}</span>
            <span className="live-skip__selected-text">
              <span className="gap-fill__selected-label">{scheduleItemTitle(item, 'Élément programmé')}</span>
              <span className="live-skip__selected-sub">
                {moved ? 'Déjà programmé — remonté' : `Déjà programmé — ${fmtTime(item.startTime)}`}
              </span>
            </span>
          </div>
          <div className="gap-fill__selected-meta">
            <span>{fmtSec(item.durationSec)}</span>
          </div>
        </div>
      ))}
    </div>
  )
})

// ─── Composant ────────────────────────────────────────────────────────────────

export const LiveSkipDialog = memo(function LiveSkipDialog({
  channelId,
  liveItem,
  nextItem,
  initialPlan,
  onConfirm,
  onCancel,
}: LiveSkipDialogProps) {
  const gridItems = useItems()
  const [options,  setOptions]  = useState<GapFillOptions | null>(null)
  const [selected, setSelected] = useState<FillItem[]>([])
  // Le compactage remonte les tampons contre la fin du direct. C'est le bon
  // réflexe pour un skip, mais pas pour une fin de direct : le plan serveur cale
  // le remplissage sur la géométrie réelle de la grille, jingle de raccord
  // compris. Compacter par-dessus déplacerait ce jingle et casserait le calage.
  const [compact,  setCompact]  = useState(!initialPlan)
  const [tab,      setTab]      = useState<CatalogTab>('ADS')
  const [loading,  setLoading]  = useState(false)
  const [saving,   setSaving]   = useState(false)
  const [error,    setError]    = useState('')
  const [now,      setNow]      = useState(getServerNowMs)
  // ── Recalage automatique à la seconde ───────────────────────────────────────
  const [auto,      setAuto]      = useState(false)
  const [armed,     setArmed]     = useState(false)
  const [autoNotice, setAutoNotice] = useState('')

  // ── Horloge temps réel ──────────────────────────────────────────────────────
  useEffect(() => {
    const timer = setInterval(() => setNow(getServerNowMs()), 1000)
    return () => clearInterval(timer)
  }, [])

  // ── Chargement des options ──────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false
    setLoading(true)
    playoutApi.getGapFillOptions(channelId)
      .then(res => { if (!cancelled) setOptions(res) })
      .catch(() => { if (!cancelled) setError('Impossible de charger les options de remplissage') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [channelId])

  // ── Bornes du trou (miroir du calcul serveur) ──────────────────────────────
  const liveEndMs        = now + LIVE_SKIP_LEAD_SEC * 1000
  const liveScheduledEnd = new Date(liveItem.endTime).getTime()
  const liveRemainingSec = Math.max(0, Math.floor((liveScheduledEnd - now) / 1000))
  /** Dépassement en cours : l'élément tient l'antenne au-delà de son heure de grille. */
  const overrunSec       = Math.max(0, Math.floor((now - liveScheduledEnd) / 1000))

  const { anchor, anchorMs, buffers, buffersSpanMs, firstPendingMs, stale } = useMemo(() => {
    const liveStartMs = new Date(liveItem.startTime).getTime()
    // Miroir de `liveSkipPendingWhere` (serveur) : ce qui finit après la coupure,
    // plus les tampons dont toute la case est passée pendant le dépassement.
    const pending = gridItems
      .filter(item => {
        if (item.id === liveItem.id || (item.status !== 'SCHEDULED' && item.status !== 'LOADING')) return false
        const endMs = new Date(item.endTime).getTime()
        if (endMs > liveEndMs) return true
        return !ANCHOR_TYPES.includes(item.itemType)
          && new Date(item.startTime).getTime() >= liveStartMs
          && endMs <= liveEndMs
      })
      .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime())

    // Tampons périmés : leur heure est passée pendant que l'élément débordait,
    // la préemption les a gardés en attente sans jamais les jouer. Ils sortent du
    // calcul du trou — le serveur les efface à la coupure
    // (`live-skip-plan.ts`) — sans quoi le trou paraissait nul et rien n'était
    // insérable.
    const perimes  = pending.filter(item => new Date(item.startTime).getTime() < liveEndMs)
    const future   = pending.filter(item => new Date(item.startTime).getTime() >= liveEndMs)

    const found    = future.find(item => ANCHOR_TYPES.includes(item.itemType)) ?? null
    const foundMs  = found ? new Date(found.startTime).getTime() : new Date(liveItem.endTime).getTime()
    const tampons  = future.filter(item =>
      !ANCHOR_TYPES.includes(item.itemType) && new Date(item.startTime).getTime() < foundMs)
    const spanMs   = tampons.length
      ? new Date(tampons[tampons.length - 1].endTime).getTime() - new Date(tampons[0].startTime).getTime()
      : 0

    return {
      anchor:         found,
      anchorMs:       foundMs,
      buffers:        tampons,
      buffersSpanMs:  spanMs,
      firstPendingMs: future.length ? new Date(future[0].startTime).getTime() : foundMs,
      stale:          perimes,
    }
  }, [gridItems, liveItem.id, liveItem.startTime, liveItem.endTime, liveEndMs])

  // Les tampons remontés emmènent le trou avec eux : il passe devant l'ancre.
  const willCompact = compact && buffers.length > 0
  const gapEndMs    = willCompact ? anchorMs : firstPendingMs
  const gapSec      = Math.max(0, Math.floor((gapEndMs - liveEndMs - (willCompact ? buffersSpanMs : 0)) / 1000))

  // Les spots publicitaires consécutifs forment un seul bloc. La transition
  // configurée entre deux spots compte dans la durée du remplissage.
  const fixedSec = useMemo(() => {
    let total = 0
    let adRun: number[] = []
    const flush = () => {
      if (adRun.length) {
        total += adBlockTotalDurationSec(adRun, options?.transitionDurationMs)
        adRun = []
      }
    }
    for (const item of selected) {
      if (item.kind === 'AD_BREAK') adRun.push(item.durationSec)
      else if (!item.fillRest) { flush(); total += item.durationSec }
      else flush()
    }
    flush()
    return total
  }, [selected, options?.transitionDurationMs])

  const hasElastic    = selected.some(item => item.fillRest)

  // ── Passage de relais à un direct de même source ────────────────────────────
  //
  // L'élément a débordé sur le créneau d'un direct du même studio, qui attend.
  // Il n'y a pas de trou à combler : la composition passe, puis ce direct part
  // derrière elle et garde sa fin de grille (`backend/.../live-handover-plan.ts`).
  const handover = useMemo(() => previewLiveHandover({
    live: {
      id:           liveItem.id,
      itemType:     liveItem.itemType,
      liveSourceId: liveItem.liveSourceId ?? null,
      liveEndMode:  liveItem.liveEndMode ?? null,
      startTime:    liveItem.startTime,
    },
    cutAtMs: liveEndMs,
    fillMs:  fixedSec * 1000,
    items:   gridItems.map(item => ({ ...item, liveSourceId: item.liveSourceId ?? null })),
  }), [liveItem.id, liveItem.itemType, liveItem.liveSourceId, liveItem.liveEndMode, liveItem.startTime, liveEndMs, fixedSec, gridItems])
  const isHandover = handover !== null
  // En relais, le direct suivant a « commencé » à l'heure de grille mais il n'est
  // ni périmé ni effacé : il est avancé derrière la composition, ou sauté.
  const visibleStale = useMemo(() => {
    if (!handover) return stale
    const handled = new Set([...(handover.next ? [handover.next.id] : []), ...handover.skipped.map(item => item.id)])
    return stale.filter(item => !handled.has(item.id))
  }, [handover, stale])

  const elasticSec    = isHandover ? 0 : Math.max(0, gapSec - fixedSec)
  const filledSec     = hasElastic ? fixedSec + elasticSec : fixedSec
  const remaining     = isHandover ? 0 : gapSec - filledSec
  const pct           = isHandover ? (selected.length > 0 ? 100 : 0) : Math.min(100, Math.round((filledSec / (gapSec || 1)) * 100))
  const isOverfill    = !isHandover && filledSec > gapSec
  const progressState = isHandover
    ? (selected.length > 0 && !hasElastic ? 'success' : 'info')
    : isOverfill ? 'danger' : remaining <= 0 ? 'success' : 'info'

  /** Ce qui empêche la coupure en passage de relais. */
  const handoverBlockReason = !isHandover
    ? ''
    : selected.length === 0
      ? 'Même source studio : ajoutez au moins un élément entre les deux directs (CasparCG doit libérer l’entrée avant de la rouvrir)'
      : hasElastic
        ? '« Combler le reste » est sans objet ici : donnez une durée à l’infoservice'
        : ''

  // ── Recalage automatique : instant de coupure ───────────────────────────────
  //
  // Le remplissage doit retomber pile sur l'ancre. On remonte donc depuis son
  // heure d'antenne — la seule qui ne bouge jamais — pour en déduire la seconde
  // où le direct doit être coupé :
  //
  //   compactage  : ancre − durée des tampons remontés − durée du remplissage
  //   sans compact: début du premier item déjà posé − durée du remplissage
  //
  // C'est l'inverse exact du calcul du trou : le direct tient l'antenne aussi
  // longtemps que possible, et l'opérateur n'a plus à viser le bon moment.
  const autoCutMs = useMemo(() => {
    const base = willCompact ? anchorMs - buffersSpanMs : firstPendingMs
    return base - fixedSec * 1000
  }, [willCompact, anchorMs, buffersSpanMs, firstPendingMs, fixedSec])

  const liveScheduledEndMs = liveScheduledEnd

  /**
   * Ce qui empêche d'armer — ou de tirer. Tant que c'est non nul, le direct
   * n'est pas coupé : mieux vaut laisser le direct à l'antenne qu'un noir.
   */
  const autoBlockReason = useMemo(() => {
    if (isHandover)           return 'Sans objet : le direct suivant part à la fin de la composition, il n’y a pas d’heure à viser'
    if (selected.length === 0) return 'Ajoutez au moins un élément : sans remplissage il n\'y a rien à caler'
    if (hasElastic)           return 'Incompatible avec « Combler tout le reste » : la durée du remplissage doit être fixe'
    if (!anchor)              return 'Aucun programme ni direct à heure fixe derrière : rien sur quoi se caler'
    if (autoCutMs >= liveScheduledEndMs)
      return 'Le direct se termine avant cet instant : la grille enchaîne toute seule, aucune coupure nécessaire'
    if (autoCutMs - now < LIVE_SKIP_MIN_LEAD_SEC * 1000)
      return 'Trop tard : le remplissage est plus long que le temps restant avant le programme suivant'
    return ''
  }, [isHandover, selected.length, hasElastic, anchor, autoCutMs, liveScheduledEndMs, now])

  // ── Callbacks ───────────────────────────────────────────────────────────────
  const addItem = useCallback((item: Omit<FillItem, 'uid'>) => {
    setSelected(prev => [...prev, { ...item, uid: nextUid() }])
  }, [])

  /**
   * Amorce de la sélection à partir du plan serveur. Une seule fois : dès que
   * l'opérateur touche à la liste, elle lui appartient et un nouveau rendu ne
   * doit pas réécrire ses choix.
   */
  const seededRef = useRef(false)
  useEffect(() => {
    if (seededRef.current || !initialPlan) return
    seededRef.current = true
    const toItem = (step: EndLivePlan['steps'][number]): FillItem => {
      if (step.itemType === 'INFOSERVICE') {
        // Élastique : la boîte reste ouverte pendant que l'horloge tourne, une
        // durée figée laisserait un trou grandissant devant le raccord.
        return {
          uid: nextUid(),
          label: step.title,
          sublabel: 'Infoservice — comble le reste',
          durationSec: 0,
          kind: 'INFOSERVICE' as FillKind,
          fillRest: true,
          entry: { itemType: 'INFOSERVICE', fillRest: true } as LiveFillEntry,
        }
      }
      return {
        uid: nextUid(),
        label: step.title,
        sublabel: step.role === 'LEAD_JINGLE' ? 'Jingle de transition' : 'Jingle de raccord',
        durationSec: step.durationSec,
        kind: 'JINGLE' as FillKind,
        entry: {
          itemType: 'JINGLE',
          durationSec: step.durationSec,
          ...(step.programId ? { programId: step.programId } : {}),
          ...(step.mediaAssetId ? { mediaAssetId: step.mediaAssetId } : {}),
        } as LiveFillEntry,
      }
    }
    // Pubs mangées par le débordement : jamais diffusées, effacées à la coupure.
    // Reproposées cochées, juste après le jingle de transition, pour ne pas
    // perdre l'engagement annonceur ; l'opérateur les retire s'il le veut.
    const eaten: FillItem[] = (initialPlan.eatenAds ?? []).map(ad => ({
      uid: nextUid(),
      label: ad.title,
      sublabel: 'Publicité non diffusée — reposée',
      durationSec: ad.durationSec,
      kind: 'AD_BREAK' as FillKind,
      entry: { itemType: 'AD_BREAK', spotId: ad.spotId, durationSec: ad.durationSec } as LiveFillEntry,
    }))
    const lead = initialPlan.steps.filter(step => step.role === 'LEAD_JINGLE').map(toItem)
    const rest = initialPlan.steps.filter(step => step.role !== 'LEAD_JINGLE').map(toItem)
    setSelected([...lead, ...eaten, ...rest])
  }, [initialPlan])

  const removeItem = useCallback((uid: string) => {
    setSelected(prev => prev.filter(item => item.uid !== uid))
  }, [])

  const moveItem = useCallback((uid: string, dir: -1 | 1) => {
    setSelected(prev => {
      const idx = prev.findIndex(item => item.uid === uid)
      const target = idx + dir
      if (idx < 0 || target < 0 || target >= prev.length) return prev
      const next = [...prev]
      const [moved] = next.splice(idx, 1)
      next.splice(target, 0, moved)
      return next
    })
  }, [])

  /**
   * Envoi effectif. `cutAtMs` porte l'instant de coupure exact (défaut serveur
   * sinon) et `requireExactFill` demande au serveur de refuser la coupure si le
   * calage n'est pas complet — le direct reste alors à l'antenne.
   */
  const submit = useCallback(async (entries: LiveFillEntry[], cutAtMs?: number) => {
    setSaving(true); setError('')
    try {
      await onConfirm(entries, {
        // Passage de relais : rien à remonter, le serveur refuserait.
        compact: compact && !isHandover,
        ...(cutAtMs !== undefined
          ? { cutAt: new Date(cutAtMs).toISOString(), requireExactFill: true }
          : { leadSec: LIVE_SKIP_LEAD_SEC }),
      })
    } catch (e: any) {
      setError(e?.message ?? 'Erreur lors du remplissage')
      setSaving(false)
      throw e
    }
  }, [onConfirm, compact, isHandover])

  const handleConfirm = useCallback(() => {
    void submit(selected.map(item => item.entry)).catch(() => {})
  }, [submit, selected])

  /** Termine l'item sans rien insérer : l'habillage automatique tiendra l'antenne. */
  const handleSkipDirect = useCallback(() => {
    void submit([]).catch(() => {})
  }, [submit])

  // ── Armement : la coupure part toute seule à la seconde calculée ────────────
  //
  // Le plan est relu à chaque battement : si la grille bouge sous nos pieds (un
  // autre poste déplace l'ancre, le remplissage n'est plus tenable…), on désarme
  // sans couper. Un direct qui déborde reste toujours préférable à un noir.
  const planRef = useRef({ autoCutMs, autoBlockReason, entries: [] as LiveFillEntry[] })
  planRef.current = {
    autoCutMs,
    autoBlockReason,
    entries: selected.map(item => item.entry),
  }

  useEffect(() => {
    if (!armed) return
    let fired = false

    const timer = setInterval(() => {
      if (fired) return
      const plan     = planRef.current
      const remainMs = plan.autoCutMs - getServerNowMs()

      if (plan.autoBlockReason) {
        setArmed(false)
        setAutoNotice(`Coupure annulée — direct maintenu à l'antenne. ${plan.autoBlockReason}`)
        return
      }
      if (remainMs < LIVE_SKIP_MIN_LEAD_SEC * 1000) {
        setArmed(false)
        setAutoNotice('Coupure annulée — le calage n\'est plus tenable, le direct reste à l\'antenne.')
        return
      }
      if (remainMs > AUTO_ARM_FIRE_WINDOW_SEC * 1000) return

      fired = true
      void submit(plan.entries, plan.autoCutMs).catch(() => {
        fired = false
        setArmed(false)
        setAutoNotice('Coupure automatique refusée par le serveur — direct maintenu à l\'antenne.')
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [armed, submit])

  const handleToggleAuto = useCallback((enabled: boolean) => {
    setAuto(enabled)
    setArmed(false)
    setAutoNotice('')
  }, [])

  const handleArm = useCallback(() => {
    setAutoNotice('')
    setArmed(true)
  }, [])

  /** Une coupure armée fige la composition : le plan ne doit pas bouger sous l'antenne. */
  const editLocked = saving || armed

  const handleDisarm = useCallback(() => {
    setArmed(false)
    setAutoNotice('Armement annulé — le direct reste à l\'antenne.')
  }, [])

  // ── Catalogue ───────────────────────────────────────────────────────────────
  const campaigns   = options?.campaigns ?? []
  const jingles     = options?.jingles ?? []
  const clips       = options?.clips ?? []
  const infoservice = options?.infoservice ?? null

  const comingNextPrograms = useMemo(() => jingles.filter(j => COMING_NEXT_TYPES.includes(j.type)), [jingles])
  const jinglePrograms     = useMemo(() => jingles.filter(j => !COMING_NEXT_TYPES.includes(j.type)), [jingles])
  const comingNextClips    = useMemo(() => clips.filter(c => c.clipType === 'COMING_NEXT'), [clips])
  const interludeClips     = useMemo(() => clips.filter(c => c.clipType !== 'COMING_NEXT'), [clips])

  const addSpot = useCallback((spot: { id: string; title: string; durationSec: number }, campaignName: string) => {
    addItem({
      label: formatAdSpotLabel({ ...spot, campaignName }), sublabel: 'Publicité', durationSec: spot.durationSec, kind: 'AD_BREAK',
      entry: { itemType: 'AD_BREAK', spotId: spot.id, durationSec: spot.durationSec },
    })
  }, [addItem])

  const addProgram = useCallback((program: { id: string; title: string; durationSec: number; mediaAssetId: string | null; type: string }) => {
    addItem({
      label: program.title, sublabel: 'Élément d’antenne', durationSec: program.durationSec, kind: 'JINGLE',
      entry: {
        itemType: 'JINGLE', programId: program.id, durationSec: program.durationSec,
        ...(program.mediaAssetId ? { mediaAssetId: program.mediaAssetId } : {}),
      },
    })
  }, [addItem])

  const addClip = useCallback((clip: { id: string; name: string; durationSec: number | null; clipType: string }) => {
    const durationSec = clip.durationSec ?? 30
    addItem({
      label: clip.name, sublabel: clip.clipType, durationSec, kind: 'INTERLUDE',
      entry: { itemType: 'INTERLUDE', clipId: clip.id, durationSec },
    })
  }, [addItem])

  const addInfoservice = useCallback((durationSec: number) => {
    if (!infoservice) return
    addItem({
      label: infoservice.title, sublabel: 'Infoservice', durationSec, kind: 'INFOSERVICE',
      entry: { itemType: 'INFOSERVICE', durationSec },
    })
  }, [addItem, infoservice])

  /** Infoservice élastique : il boucle jusqu'au programme suivant. */
  const addInfoserviceRest = useCallback(() => {
    if (!infoservice) return
    addItem({
      label: infoservice.title, sublabel: 'Infoservice — comble le reste', durationSec: 0,
      kind: 'INFOSERVICE', fillRest: true,
      entry: { itemType: 'INFOSERVICE', fillRest: true },
    })
  }, [addItem, infoservice])

  // ── Rendu ───────────────────────────────────────────────────────────────────
  return (
    <ModalShell
      className="agenda-editor gap-fill live-skip"
      closeDisabled={saving}
      dismissOnOverlay={false}
      onClose={onCancel}
      showCloseButton={false}
      size="wide"
      title="Skip — Remplissage de l'antenne"
    >
      <div className="gap-fill__body">

        {/* ── Indicateur LIVE + compte à rebours ── */}
        <div className="gap-fill__summary">
          <div>
            <div className="live-skip__live-indicator">
              {liveItem.itemType === 'LIVE' ? 'DIRECT EN COURS' : 'ÉLÉMENT EN COURS'}
            </div>
            <div className="gap-fill__summary-copy">
              {overrunSec > 0
                ? <>Dépassement : <strong>{fmtSec(overrunSec)}</strong></>
                : <>Temps restant : <strong>{fmtSec(liveRemainingSec)}</strong></>}
              {handover
                ? handover.next
                  ? <>{' · '}▶ <strong>{itemTitle(handover.next)}</strong> démarrera à <strong>{fmtTime(handover.nextStartMs)}</strong> (fin {fmtTime(handover.next.endTime)} inchangée)</>
                  : <>{' · '}Plus assez de temps pour le direct suivant : il sera sauté</>
                : <>{' · '}Trou à combler jusqu'à <strong>{fmtTime(gapEndMs)}</strong> : <strong>{fmtSec(gapSec)}</strong></>}
            </div>
            {handover && handover.skipped.length > 0 && (
              <div className="gap-fill__summary-copy">
                ⚠ Créneau entièrement consommé, non diffusé : {handover.skipped.map(item => itemTitle(item)).join(', ')}
              </div>
            )}
            {visibleStale.length > 0 && (
              <div className="gap-fill__summary-copy">
                ⚠ {visibleStale.length === 1 ? '1 élément périmé sera retiré' : `${visibleStale.length} éléments périmés seront retirés`}
                {' ('}{visibleStale.map(item => itemTitle(item)).join(', ')}{') : '}
                leur heure d'antenne est passée pendant le dépassement. Reposez-les
                dans le trou si vous y tenez.
              </div>
            )}
          </div>
          <div className={`gap-fill__status is-${progressState}`} aria-live="polite">
            <div className="gap-fill__status-value">
              {isHandover ? fmtSec(fixedSec) : <>{fmtSec(filledSec)} / {fmtSec(gapSec)}</>}
            </div>
            <div className="gap-fill__status-detail">
              {isHandover
                ? handoverBlockReason ? 'Composition à compléter' : '✓ Puis le direct suivant'
                : isOverfill ? `Dépassement de ${fmtSec(filledSec - gapSec)}` : remaining > 0 ? `Reste ${fmtSec(remaining)}` : '✓ Trou comblé'}
            </div>
          </div>
        </div>

        {/* ── Barre de progression ── */}
        <div className="gap-fill__progress" data-state={progressState}>
          <div className="gap-fill__progress-value" style={{ '--gap-fill-progress': `${pct}%` } as CSSProperties} />
        </div>

        <div className="gap-fill__layout live-skip__layout">

          {/* ── Colonne gauche : mini-grille + sélection ── */}
          <div className="gap-fill__selection-column">
            <div className="gap-fill__preview">
              <div className="agenda-editor__eyebrow">Aperçu du remplissage</div>

              <div className="gap-fill__boundary gap-fill__boundary--live">
                <span>🔴 {scheduleItemTitle(liveItem, 'Élément')} (fin)</span>
                <span className="gap-fill__boundary-time">dans {LIVE_SKIP_LEAD_SEC} s</span>
              </div>

              {/* Tampons remontés : ils passent avant le remplissage. */}
              {!isHandover && willCompact && <BufferRows items={buffers} moved />}

              <div className="gap-fill__selected-list">
                {selected.length === 0 ? (
                  <div className="agenda-editor__empty agenda-editor__empty--compact">
                    Choisissez un contenu à droite pour l'ajouter ici
                  </div>
                ) : (
                  selected.map((item, idx) => (
                    <div key={item.uid} className="gap-fill__selected-row" data-last={idx === selected.length - 1 || undefined}>
                      <div className="gap-fill__selected-main">
                        <span className={`gap-fill__kind is-${item.kind.toLowerCase()}`}>{KIND_BADGES[item.kind]}</span>
                        <span className="live-skip__selected-text">
                          <span className="gap-fill__selected-label">{item.label}</span>
                          <span className="live-skip__selected-sub">{item.sublabel}</span>
                        </span>
                      </div>
                      <div className="gap-fill__selected-meta">
                        <span>{fmtSec(item.fillRest ? elasticSec : item.durationSec)}</span>
                        <button
                          type="button" className="tp-icon-button live-skip__reorder"
                          onClick={() => moveItem(item.uid, -1)} disabled={editLocked || idx === 0}
                          aria-label={`Monter ${item.label}`}
                        >↑</button>
                        <button
                          type="button" className="tp-icon-button live-skip__reorder"
                          onClick={() => moveItem(item.uid, 1)} disabled={editLocked || idx === selected.length - 1}
                          aria-label={`Descendre ${item.label}`}
                        >↓</button>
                        <button
                          type="button" className="tp-icon-button gap-fill__remove"
                          onClick={() => removeItem(item.uid)} disabled={editLocked}
                          aria-label={`Retirer ${item.label}`}
                        >×</button>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Tampons laissés en place : ils restent après le remplissage. */}
              {!isHandover && !willCompact && buffers.length > 0 && <BufferRows items={buffers} />}

              <div className="gap-fill__boundary gap-fill__boundary--next">
                {handover?.next ? (
                  <>
                    <span>▶ {itemTitle(handover.next)} (même studio)</span>
                    <span className="gap-fill__boundary-time">{fmtTime(handover.nextStartMs)}</span>
                  </>
                ) : (
                  <>
                    <span>▶ {itemTitle(anchor ?? nextItem)}</span>
                    <span className="gap-fill__boundary-time">{fmtTime(anchorMs)}</span>
                  </>
                )}
              </div>

              {!isHandover && buffers.length > 0 && (
                <label className="live-skip__compact">
                  <input
                    type="checkbox" checked={compact} disabled={editLocked}
                    onChange={event => setCompact(event.target.checked)}
                  />
                  <span>
                    Remonter les {buffers.length} élément{buffers.length > 1 ? 's' : ''} déjà programmé
                    {buffers.length > 1 ? 's' : ''} contre la fin de l'élément
                    <span className="live-skip__compact-hint">
                      {willCompact
                        ? `Pubs, éléments d’antenne et infoservice avancent de ${fmtSec(Math.max(0, Math.floor((new Date(buffers[0].startTime).getTime() - liveEndMs) / 1000)))} — le trou se reporte juste avant « ${itemTitle(anchor ?? nextItem)} ».`
                        : 'Ils gardent leur heure actuelle, le trou reste juste après l\'élément.'}
                    </span>
                  </span>
                </label>
              )}

              {/* ── Recalage automatique à la seconde ── */}
              <label className="live-skip__auto">
                <input
                  type="checkbox" checked={auto && !isHandover} disabled={saving || armed || isHandover}
                  onChange={event => handleToggleAuto(event.target.checked)}
                />
                <span>
                  Recaler automatiquement à la seconde
                  <span className="live-skip__compact-hint">
                    {isHandover
                      ? 'Sans objet : le direct suivant part à la fin de la composition, il n’y a pas d’heure à viser.'
                      : <>
                          Plus besoin de viser le bon moment : la coupure part toute seule à l'instant
                          qui fait retomber le remplissage pile sur « {itemTitle(anchor ?? nextItem)} ».
                          Si le calage n'est pas complet, le direct <strong>n'est pas coupé</strong>.
                        </>}
                  </span>
                </span>
              </label>

              {auto && !isHandover && (
                <div
                  className="live-skip__auto-status"
                  data-state={autoBlockReason ? 'blocked' : armed ? 'armed' : 'ready'}
                  role="status" aria-live="polite"
                >
                  {autoBlockReason ? (
                    <>⚠ {autoBlockReason}</>
                  ) : armed ? (
                    <>
                      ⏱ <strong>Armé</strong> — coupure du direct à <strong>{fmtTime(autoCutMs)}</strong>
                      {' '}(dans {fmtSec(Math.max(0, Math.round((autoCutMs - now) / 1000)))})
                    </>
                  ) : (
                    <>
                      Coupure prévue à <strong>{fmtTime(autoCutMs)}</strong>
                      {' '}(dans {fmtSec(Math.max(0, Math.round((autoCutMs - now) / 1000)))})
                      {' · '}remplissage de {fmtSec(fixedSec)} jusqu'à {fmtTime(anchorMs)}
                    </>
                  )}
                </div>
              )}

              {autoNotice && (
                <div className="agenda-editor__notice is-warning" role="alert">
                  ⚠ {autoNotice}
                </div>
              )}

              {isHandover && handoverBlockReason && (
                <div className="live-skip__auto-status" data-state="blocked" role="status" aria-live="polite">
                  ⚠ {handoverBlockReason}
                </div>
              )}

              <p className="live-skip__hint">
                L'élément est terminé {LIVE_SKIP_LEAD_SEC} s après la confirmation, le temps de
                précharger le premier élément.
                {isHandover
                  ? ' Le direct suivant, sur le même studio, part juste après la composition et garde sa fin de grille : rien de ce qui le suit ne bouge.'
                  : ' Les programmes et directs suivants gardent leur heure d\'antenne : ils ne bougent jamais.'}
                {auto && !isHandover && ' Une fois armée, gardez cette fenêtre ouverte : la fermer annule la coupure automatique.'}
              </p>
            </div>

            {error && (
              <div className="agenda-editor__notice is-danger" role="alert">
                ⚠ {error}
              </div>
            )}

            <div className="agenda-editor__actions live-skip__actions">
              <button type="button" className="tp-button tp-button--ghost" onClick={onCancel} disabled={saving}>
                Annuler
              </button>
              <button
                type="button" className="tp-button tp-button--outline-warning"
                onClick={handleSkipDirect} disabled={saving || armed || isHandover}
                title={isHandover
                  ? 'Même studio : un élément doit passer entre les deux directs'
                  : 'Termine l\'élément sans rien insérer : l\'habillage automatique tient l\'antenne jusqu\'au programme suivant'}
              >
                {saving ? '…' : 'Couper sans remplir →'}
              </button>
              {auto && !isHandover ? (
                armed ? (
                  <button
                    type="button" className="tp-button tp-button--outline-warning"
                    onClick={handleDisarm} disabled={saving}
                  >
                    {saving ? 'Coupure…' : '✕ Désarmer'}
                  </button>
                ) : (
                  <button
                    type="button" className="tp-button tp-button--primary"
                    onClick={handleArm} disabled={saving || autoBlockReason !== ''}
                    title="La coupure partira toute seule à la seconde calculée"
                  >
                    ⏱ Armer la coupure automatique
                  </button>
                )
              ) : (
                <button
                  type="button" className="tp-button tp-button--primary"
                  onClick={handleConfirm} disabled={saving || selected.length === 0 || isOverfill || handoverBlockReason !== ''}
                >
                  {saving ? 'Insertion…' : isHandover ? '✓ Couper et enchaîner' : '✓ Confirmer le remplissage'}
                </button>
              )}
            </div>
          </div>

          {/* ── Colonne droite : contenu disponible ── */}
          <div className="gap-fill__catalog live-skip__catalog">
            <div className="live-skip__tabs" role="tablist" aria-label="Contenu disponible">
              {CATALOG_TABS.map(entry => (
                <button
                  key={entry.id}
                  type="button"
                  role="tab"
                  aria-selected={tab === entry.id}
                  className="live-skip__tab"
                  data-active={tab === entry.id || undefined}
                  onClick={() => setTab(entry.id)}
                >
                  {entry.label}
                </button>
              ))}
            </div>

            <div className="live-skip__catalog-list">
              {loading && <div className="agenda-editor__loading"><Spinner /> Chargement…</div>}

              {/* ── Publicités ── */}
              {!loading && tab === 'ADS' && (
                campaigns.length === 0 ? (
                  <div className="agenda-editor__empty">Aucune campagne active</div>
                ) : campaigns.map(campaign => (
                  <div key={campaign.id} className="live-skip__group">
                    <div className="gap-fill__group-title">
                      {campaign.name}
                      {campaign.advertiserName ? ` · ${campaign.advertiserName}` : ''}
                    </div>
                    {campaign.spots.length === 0 ? (
                      <div className="agenda-editor__empty agenda-editor__empty--compact">Aucun spot actif</div>
                    ) : (
                      <>
                        {campaign.spots.map(spot => (
                          <button
                            key={spot.id} type="button" className="gap-fill__available"
                            onClick={() => addSpot(spot, campaign.name)} disabled={editLocked}
                          >
                            <div className="gap-fill__available-title">{formatAdSpotLabel({ ...spot, campaignName: campaign.name })}</div>
                            <div className="gap-fill__available-meta">PUB · {fmtSec(spot.durationSec)}</div>
                          </button>
                        ))}
                        {campaign.spots.length > 1 && (
                          <button
                            type="button" className="live-skip__add-all"
                            onClick={() => campaign.spots.forEach(spot => addSpot(spot, campaign.name))}
                            disabled={editLocked}
                          >
                            + Tout le bloc ({campaign.spotCount} spots · {fmtSec(campaign.totalDurationSec)})
                          </button>
                        )}
                      </>
                    )}
                  </div>
                ))
              )}

              {/* ── Infoservice ── */}
              {!loading && tab === 'INFOSERVICE' && (
                !infoservice ? (
                  <div className="agenda-editor__empty">Aucun infoservice sélectionné pour cette chaîne</div>
                ) : (
                  <>
                    <div className="gap-fill__group-title">
                      Infoservice sélectionné{infoservice.mediaDate ? ` · ${infoservice.mediaDate}` : ''}
                    </div>
                    <button
                      type="button" className="gap-fill__available"
                      onClick={() => addInfoservice(infoservice.durationSec)} disabled={editLocked}
                    >
                      <div className="gap-fill__available-title">{infoservice.title}</div>
                      <div className="gap-fill__available-meta">INFOSERVICE · {fmtSec(infoservice.durationSec)}</div>
                    </button>
                    <button
                      type="button" className="gap-fill__available"
                      onClick={addInfoserviceRest} disabled={editLocked || hasElastic || remaining <= 0 || isHandover}
                    >
                      <div className="gap-fill__available-title">Combler tout le reste</div>
                      <div className="gap-fill__available-meta">
                        {isHandover
                          ? 'Sans objet : le direct suivant part à la fin de la composition'
                          : hasElastic
                          ? 'Déjà dans le remplissage'
                          : remaining > 0
                            ? `L'infoservice boucle jusqu'au programme suivant · ${fmtSec(remaining)}`
                            : 'Le trou est déjà comblé'}
                      </div>
                    </button>
                  </>
                )
              )}

              {/* ── Jingles ── */}
              {!loading && tab === 'JINGLES' && (
                jinglePrograms.length === 0 && interludeClips.length === 0 ? (
                  <div className="agenda-editor__empty">Aucun élément d’antenne disponible</div>
                ) : (
                  <>
                    {jinglePrograms.length > 0 && <div className="gap-fill__group-title">Éléments d’antenne</div>}
                    {jinglePrograms.map(program => (
                      <button
                        key={program.id} type="button" className="gap-fill__available"
                        onClick={() => addProgram(program)} disabled={editLocked}
                      >
                        <div className="gap-fill__available-title">{program.title}</div>
                        <div className="gap-fill__available-meta">{program.type} · {fmtSec(program.durationSec)}</div>
                      </button>
                    ))}
                    {interludeClips.length > 0 && <div className="gap-fill__group-title">Interludes</div>}
                    {interludeClips.map(clip => (
                      <button
                        key={clip.id} type="button" className="gap-fill__available"
                        onClick={() => addClip(clip)} disabled={editLocked}
                      >
                        <div className="gap-fill__available-title">{clip.name}</div>
                        <div className="gap-fill__available-meta">{clip.clipType} · {clip.durationSec ? fmtSec(clip.durationSec) : '?'}</div>
                      </button>
                    ))}
                  </>
                )
              )}

              {/* ── Coming next ── */}
              {!loading && tab === 'COMING_NEXT' && (
                comingNextPrograms.length === 0 && comingNextClips.length === 0 ? (
                  <div className="agenda-editor__empty">Aucun coming next disponible</div>
                ) : (
                  <>
                    {comingNextPrograms.map(program => (
                      <button
                        key={program.id} type="button" className="gap-fill__available"
                        onClick={() => addProgram(program)} disabled={editLocked}
                      >
                        <div className="gap-fill__available-title">{program.title}</div>
                        <div className="gap-fill__available-meta">{program.type} · {fmtSec(program.durationSec)}</div>
                      </button>
                    ))}
                    {comingNextClips.map(clip => (
                      <button
                        key={clip.id} type="button" className="gap-fill__available"
                        onClick={() => addClip(clip)} disabled={editLocked}
                      >
                        <div className="gap-fill__available-title">{clip.name}</div>
                        <div className="gap-fill__available-meta">COMING NEXT · {clip.durationSec ? fmtSec(clip.durationSec) : '?'}</div>
                      </button>
                    ))}
                  </>
                )
              )}
            </div>
          </div>
        </div>
      </div>
    </ModalShell>
  )
})
